import { useEffect, useState } from 'react';
import type { Round } from '@h2h/shared';
import { RoundDatePager, useRoundDateTabs, defaultRoundDateTabId } from '../RoundDatePager';
import { Placeholder } from '../../ui/Placeholder';
import { Trophy, ChevronRight } from '../../ui/icons';
import { useMyGroup, useMySeasonResults } from '../../../hooks/useQueries';
import { LiveRoundStandingsView } from './LiveRoundStandingsView';

/**
 * The Division view, dated. A pager runs your past rounds oldest → newest with
 * "Now" pinned last (the same model as the League Hub division card), so you can
 * flip back and see the group you were drawn into for any round and how you ended
 * it — the live board for "Now", the final table + verdict for a past date.
 *
 * Each round reshuffles the groups, so a past tab is a different set of rivals; the
 * DivisionHero already carries the per-round verdict (rank, points, movement), so
 * no separate recap card is needed here.
 */
export function DivisionDatedView({
  leagueId,
  uid,
  currentRound,
  membershipTier,
  isCycling,
  liveProgress,
  roundOpen,
  hasEntry,
  onOpenRound,
  onEnterRound
}: {
  leagueId: string | undefined;
  uid: string;
  currentRound: Round | null | undefined;
  membershipTier?: number;
  isCycling: boolean;
  /** Fixtures settled vs total for the LIVE current round (ignored on past dates). */
  liveProgress: { settled: number; total: number } | null;
  roundOpen?: boolean;
  hasEntry?: boolean;
  onOpenRound: (roundId: string) => void;
  onEnterRound?: () => void;
}) {
  const { data: seasonResults } = useMySeasonResults(leagueId, uid);
  const tabs = useRoundDateTabs({ currentRound, seasonResults, isCycling });

  const currentRoundId = currentRound?.roundId ?? null;
  // "Now" stays in the pager, but until it has any scores we default to the latest
  // settled round so the opener doesn't land on an empty "No scores yet" board.
  const defaultId = defaultRoundDateTabId(tabs, liveProgress) ?? currentRoundId ?? null;
  const [pickedId, setPickedId] = useState<string | null>(null);
  useEffect(() => setPickedId(null), [currentRoundId]);
  const selectedId = pickedId ?? defaultId;
  const selectedTab = tabs.find((t) => t.roundId === selectedId) ?? tabs[tabs.length - 1] ?? null;
  const isLiveSelected = selectedTab?.isLive ?? false;

  const { data: group } = useMyGroup(leagueId, selectedTab?.roundId ?? currentRoundId, uid, isLiveSelected);

  const selectedRound: Round | null = isLiveSelected
    ? currentRound ?? null
    : seasonResults?.find((r) => r.round.roundId === selectedTab?.roundId)?.round ?? null;
  const roundLabel = selectedRound?.label ?? selectedTab?.label ?? 'this round';

  return (
    <>
      {tabs.length > 1 && (
        <RoundDatePager tabs={tabs} selectedId={selectedTab?.roundId ?? null} onSelect={setPickedId} />
      )}

      {group && uid ? (
        <>
          <LiveRoundStandingsView
            group={group}
            uid={uid}
            roundLabel={roundLabel}
            // Past rounds: use the tier they actually played that round (group.tier);
            // the live round shows the player's current ladder rung.
            playerTier={isLiveSelected ? membershipTier ?? group.tier : group.tier}
            live={isLiveSelected}
            liveProgress={isLiveSelected ? liveProgress : null}
          />
          {!isLiveSelected && selectedTab && (
            <button
              type="button"
              onClick={() => onOpenRound(selectedTab.roundId)}
              className="tap flex w-full items-center justify-center gap-2 rounded-2xl border border-black/5 bg-card py-[13px] shadow-tile transition-transform active:scale-[0.99]"
            >
              <span className="text-[13px] font-extrabold tracking-snug text-ink">
                {isCycling ? 'Open stage breakdown' : 'Open round breakdown'}
              </span>
              <ChevronRight width={15} height={15} strokeWidth={2.6} className="text-ink3" />
            </button>
          )}
        </>
      ) : (
        <>
          <Placeholder
            Icon={Trophy}
            title={isLiveSelected ? 'No live group yet' : 'No group this round'}
            body={
              isLiveSelected
                ? roundOpen && !hasEntry
                  ? isCycling
                    ? 'Call your duels while the stage is open — your division board appears once you’re in.'
                    : 'Build your cards while the round is open — your division board appears once you’re in.'
                  : 'When a round opens you’ll be slotted into a division group and your live board shows here.'
                : 'You weren’t grouped for this round, so there’s no division table to show.'
            }
          />
          {isLiveSelected && roundOpen && !hasEntry && onEnterRound && (
            <button
              type="button"
              onClick={onEnterRound}
              className="tap flex w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] py-[14px] shadow-[0_16px_30px_-16px_rgba(11,160,120,0.55)]"
            >
              <span className="text-[14px] font-extrabold text-white">
                {isCycling ? 'Call your duels' : 'Build your cards'}
              </span>
              <ChevronRight width={15} height={15} strokeWidth={2.6} className="text-white" />
            </button>
          )}
        </>
      )}
    </>
  );
}
